import { useTheme } from '@mui/material/styles';

import { Chart, type ChartTableColumn } from './Chart';
import {
  baseChartOptions,
  categoryAxis,
  schemePalette,
  useChartScheme,
  valueAxis,
} from './chartTheme';
import type { ChartSeries } from './series';

export interface StackedShareBarProps {
  readonly ariaLabel: string;
  readonly categories: readonly string[];
  readonly rowHeader: string;
  readonly series: readonly ChartSeries[];
  readonly tableCaption: string;
}

export function StackedShareBar({
  ariaLabel,
  categories,
  rowHeader,
  series,
  tableCaption,
}: StackedShareBarProps) {
  const theme = useTheme();
  const scheme = useChartScheme();
  const base = baseChartOptions(theme, scheme);
  // Not `theme.palette`'s background.paper: under cssVariables that object is pinned to light, so in
  // dark mode the segment gaps would be painted with the light surface colour and read as white seams.
  const surface = schemePalette(theme, scheme).background.paper;
  const columns: readonly ChartTableColumn[] = series.map((entry) => ({
    header: entry.name,
    values: entry.labels,
  }));

  // Each row is normalised to its own 100%. Rows here are currencies, and plotting raw amounts on one
  // shared axis would compare THB against USD by bar length — a comparison that means nothing. A
  // proportion does carry across currencies, and it is what "share" in the name refers to. The exact
  // amounts stay in `labels`, which feed both the tooltip and the accessible table.
  const rowTotals = categories.map((_category, index) =>
    series.reduce((total, entry) => total + (entry.values[index] ?? 0), 0),
  );
  const share = (value: number, index: number): number => {
    const total = rowTotals[index] ?? 0;
    return total === 0 ? 0 : (value / total) * 100;
  };
  const percentFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

  return (
    <Chart
      ariaLabel={ariaLabel}
      columns={columns}
      height={Math.max(120, categories.length * 56)}
      option={{
        ...base,
        legend: { ...base.legend, data: series.map((entry) => entry.name) },
        series: series.map((entry, seriesIndex) => ({
          barMaxWidth: 24,
          data: entry.values.map((value, index) => share(value, index)),
          itemStyle: {
            // A 2px surface-coloured gap separates touching segments. The gap does the separating —
            // never a stroke, which would add ink that is not data.
            borderColor: surface,
            borderWidth: 2,
            borderRadius: seriesIndex === series.length - 1 ? [0, 4, 4, 0] : 0,
          },
          name: entry.name,
          stack: 'share',
          type: 'bar' as const,
        })),
        tooltip: {
          ...base.tooltip,
          // The bar carries the proportion; the tooltip carries the actual amount, from `labels`. Both
          // are shown, so the reader never has to infer a figure from a bar width.
          formatter: (params: { dataIndex: number; seriesName: string; value: number }) => {
            const match = series.find((candidate) => candidate.name === params.seriesName);
            const amount = match?.labels[params.dataIndex] ?? '';
            const percent = percentFormat.format(params.value);
            return `${categories[params.dataIndex] ?? ''}<br/>${params.seriesName}: ${amount} (${percent}%)`;
          },
          trigger: 'item' as const,
        },
        xAxis: {
          ...valueAxis(theme, scheme, (value) => `${percentFormat.format(value)}%`),
          max: 100,
          splitLine: { show: false },
        },
        yAxis: { ...categoryAxis(theme, scheme), data: [...categories] },
      }}
      rowHeader={rowHeader}
      rows={categories}
      tableCaption={tableCaption}
    />
  );
}
